import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { html, nothing, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { wheelDeltaToPixels } from '../table/utils/wheel.js';

const MIN_THUMB_WIDTH = 32;

/**
 * Pixel delta a wheel event contributes to a container whose horizontal
 * axis is scrolled by hand (`overflowX: 'hidden'`), or `null` when the
 * gesture is zoom (meta/ctrl) or mainly vertical. Shift turns a vertical
 * wheel into a horizontal scroll, as native scrolling does.
 */
export function getHorizontalWheelDelta(
  event: WheelEvent,
  pageWidth: number
): number | null {
  if (event.metaKey || event.ctrlKey) return null;
  const rawDeltaX = event.deltaX;
  const rawDeltaY = event.deltaY;
  const deltaX =
    event.shiftKey && Math.abs(rawDeltaY) > Math.abs(rawDeltaX)
      ? rawDeltaY
      : rawDeltaX;
  const deltaY = event.shiftKey ? 0 : rawDeltaY;
  if (Math.abs(deltaX) <= Math.abs(deltaY)) return null;
  return wheelDeltaToPixels(deltaX, event.deltaMode, pageWidth);
}

const horizontalScrollbarStyle = css({
  position: 'relative',
  display: 'block',
  flex: '0 0 16px',
  height: '16px',
  minHeight: '16px',
  cursor: 'grab',
  touchAction: 'none',
  userSelect: 'none',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'var(--streamlain-v2-layer-background-hoverOverlay)',
  },
  '&:active': {
    cursor: 'grabbing',
  },
  '&[hidden]': {
    display: 'none',
  },
});

const horizontalScrollbarThumbStyle = css({
  position: 'absolute',
  top: '5px',
  left: 0,
  height: '6px',
  minWidth: `${MIN_THUMB_WIDTH}px`,
  borderRadius: '3px',
  backgroundColor: 'var(--streamlain-v2-layer-blackAlpha-30)',
  pointerEvents: 'none',
});

/**
 * Always-visible horizontal scrollbar for a data-view scroll container
 * whose native horizontal bar is hidden (`overflowX: 'hidden'`). Overlay
 * scrollbars (macOS default) would otherwise leave wide views without any
 * visible horizontal affordance.
 *
 * The owner passes the container and its measured widths; the scrollbar
 * follows the container's own `scroll` events, so programmatic scrolling
 * by the owner moves the thumb without re-rendering the owner. The host
 * is the track, is hidden while nothing overflows, and occupies 16px in
 * the owner's flex column otherwise.
 */
export class DataViewHorizontalScrollbar extends ShadowlessElement {
  @property({ attribute: false })
  accessor target: HTMLElement | undefined;

  @property({ attribute: false })
  accessor viewportWidth = 0;

  @property({ attribute: false })
  accessor contentWidth = 0;

  @property({ attribute: false })
  accessor label = '';

  @state()
  private accessor targetScrollLeft = 0;

  private observedTarget: HTMLElement | undefined;

  private readonly onTargetScroll = () => {
    this.targetScrollLeft = this.observedTarget?.scrollLeft ?? 0;
  };

  private readonly onKeyDown = (event: KeyboardEvent) => {
    const container = this.target;
    if (!container) return;
    const step = event.key === 'PageDown' || event.key === 'PageUp' ? 0.8 : 0.1;
    const direction =
      event.key === 'ArrowRight' || event.key === 'PageDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'PageUp'
          ? -1
          : 0;
    if (direction === 0) return;
    event.preventDefault();
    container.scrollLeft += direction * container.clientWidth * step;
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const container = this.target;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (maxScrollLeft <= 0) return;

    const trackRect = this.getBoundingClientRect();
    const thumbWidth = Math.max(
      MIN_THUMB_WIDTH,
      (container.clientWidth / container.scrollWidth) * trackRect.width
    );
    const maxThumbLeft = Math.max(1, trackRect.width - thumbWidth);
    const pointerToScrollLeft = (clientX: number) =>
      (Math.max(
        0,
        Math.min(maxThumbLeft, clientX - trackRect.left - thumbWidth / 2)
      ) /
        maxThumbLeft) *
      maxScrollLeft;
    const startPointerX = event.clientX;
    const startScrollLeft = container.scrollLeft;
    const startedOnThumb =
      event.clientX - trackRect.left >=
        (startScrollLeft / maxScrollLeft) * maxThumbLeft &&
      event.clientX - trackRect.left <=
        (startScrollLeft / maxScrollLeft) * maxThumbLeft + thumbWidth;

    if (!startedOnThumb) {
      container.scrollLeft = pointerToScrollLeft(event.clientX);
    }
    const dragStartScrollLeft = container.scrollLeft;
    const dragStartPointerX = startedOnThumb ? startPointerX : event.clientX;

    const onPointerMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientX - dragStartPointerX;
      container.scrollLeft = Math.max(
        0,
        Math.min(
          maxScrollLeft,
          dragStartScrollLeft + (delta / maxThumbLeft) * maxScrollLeft
        )
      );
    };
    const stopDragging = () => {
      this.removeEventListener('pointermove', onPointerMove);
      this.removeEventListener('pointerup', stopDragging);
      this.removeEventListener('pointercancel', stopDragging);
    };

    event.preventDefault();
    this.setPointerCapture(event.pointerId);
    this.addEventListener('pointermove', onPointerMove);
    this.addEventListener('pointerup', stopDragging);
    this.addEventListener('pointercancel', stopDragging);
  };

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(horizontalScrollbarStyle);
    this.setAttribute('role', 'scrollbar');
    this.setAttribute('aria-orientation', 'horizontal');
    this.setAttribute('aria-valuemin', '0');
    this.tabIndex = 0;
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.observeTarget(this.target);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.observeTarget(undefined);
  }

  protected override willUpdate(changed: PropertyValues<this>) {
    if (changed.has('target') && this.isConnected) {
      this.observeTarget(this.target);
    }
  }

  private observeTarget(target: HTMLElement | undefined) {
    if (this.observedTarget === target) return;
    this.observedTarget?.removeEventListener('scroll', this.onTargetScroll);
    this.observedTarget = target;
    target?.addEventListener('scroll', this.onTargetScroll, { passive: true });
    this.targetScrollLeft = target?.scrollLeft ?? 0;
  }

  private get metrics() {
    const { viewportWidth, contentWidth } = this;
    if (viewportWidth <= 0 || contentWidth <= viewportWidth) return null;
    const maxScrollLeft = contentWidth - viewportWidth;
    const scrollLeft = Math.min(
      maxScrollLeft,
      Math.max(0, this.targetScrollLeft)
    );
    const thumbWidth = Math.max(
      MIN_THUMB_WIDTH,
      (viewportWidth / contentWidth) * viewportWidth
    );
    const maxThumbLeft = Math.max(0, viewportWidth - thumbWidth);
    return {
      maxScrollLeft,
      scrollLeft,
      thumbLeft: (scrollLeft / maxScrollLeft) * maxThumbLeft,
      thumbWidth,
    };
  }

  protected override updated() {
    const metrics = this.metrics;
    this.hidden = !metrics;
    this.setAttribute('aria-label', this.label);
    this.setAttribute('aria-valuemax', String(metrics?.maxScrollLeft ?? 0));
    this.setAttribute('aria-valuenow', String(metrics?.scrollLeft ?? 0));
  }

  override render() {
    const metrics = this.metrics;
    if (!metrics) return nothing;
    return html`<div
      class="${horizontalScrollbarThumbStyle}"
      style="${styleMap({
        transform: `translate3d(${metrics.thumbLeft}px, 0, 0)`,
        width: `${metrics.thumbWidth}px`,
      })}"
    ></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-horizontal-scrollbar': DataViewHorizontalScrollbar;
  }
}
